import React, { useRef } from 'react';
import { useInView } from '../hooks/useMotion';

const services = [
  { title: 'Promotion- und Verkaufsteams', desc: 'Geschulte Teams für maximale Sichtbarkeit und Abschlüsse.', img: 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?auto=format&fit=crop&w=900&q=80' },
  { title: 'Verkaufsförderung (POS)',       desc: 'Wirksame Aktionen direkt am Point of Sale.',              img: 'https://images.unsplash.com/photo-1580674287405-80cd77a2fee2?auto=format&fit=crop&w=900&q=80' },
  { title: 'Roadshows & Events',            desc: 'Deutschlandweite Kampagnen, souverän umgesetzt.',        img: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=900&q=80' },
  { title: 'Messepersonal',                 desc: 'Professionelle Präsenz auf Ihrer Messe.',                img: 'https://images.pexels.com/photos/860227/pexels-photo-860227.jpeg?auto=compress&cs=tinysrgb&w=900' },
  { title: 'Hostessen & Hosts',             desc: 'Repräsentativ, freundlich, zuverlässig.',                img: 'https://images.unsplash.com/photo-1760324457032-cdbf91381531?auto=format&fit=crop&w=900&q=80' },
  { title: 'Sampling-Aktionen',             desc: 'Produkte gezielt in die Hände Ihrer Zielgruppe.',        img: 'https://images.unsplash.com/photo-1777731052038-f185fb0c3292?auto=format&fit=crop&w=900&q=80' },
  { title: 'Merchandising',                 desc: 'Sauberer Auftritt Ihrer Marke in der Fläche.',           img: 'https://images.unsplash.com/photo-1578916171728-46686eac8d58?auto=format&fit=crop&w=900&q=80' },
  { title: 'Vertriebsunterstützung',        desc: 'Mehr Reichweite, mehr Umsatz – planbar.',                img: 'https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?auto=compress&cs=tinysrgb&w=900' },
  { title: 'Telekommunikation',             desc: 'Unsere Kernkompetenz seit über 15 Jahren.',              img: 'https://images.unsplash.com/photo-1556740767-414a9c4860c1?auto=format&fit=crop&w=900&q=80' },
  { title: 'Marketing- und Werbeaktionen',  desc: 'Von Konzept bis Durchführung – aus einer Hand.',         img: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?auto=format&fit=crop&w=900&q=80' },
  { title: 'Individuelle Promotion-Konzepte', desc: 'Maßgeschneidert für jede Branche und Zielgruppe.',     img: 'https://images.pexels.com/photos/6476257/pexels-photo-6476257.jpeg?auto=compress&cs=tinysrgb&w=900' }
];

// 3D tilt on mouse move (subtle, premium feeling)
const useTilt = () => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-py * 4).toFixed(2)}deg) rotateY(${(px * 5).toFixed(2)}deg) translateY(-3px)`;
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = '';
  };
  return { ref, onMove, onLeave };
};

const ServiceCard = ({ img, title, desc, index, inView }) => {
  const tilt = useTilt();
  return (
    <article
      ref={tilt.ref}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      className={`pp-card group relative rounded-[20px] bg-white border border-[#E7E8EC] overflow-hidden will-change-transform transition-[transform,box-shadow,border-color] duration-500 ease-out hover:border-[#111111] hover:shadow-[0_24px_50px_-28px_rgba(17,17,17,0.45)] ${
        inView ? 'pp-reveal-in' : 'pp-reveal'
      }`}
      style={{ transitionDelay: inView ? `${Math.min(index * 60, 480)}ms` : '0ms' }}
    >
      {/* Cursor spotlight */}
      <div className="pp-card-spot pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative aspect-[4/3] overflow-hidden bg-[#F6F7F9]">
        <img
          src={img}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          loading="lazy"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-black/0 to-transparent" />
        {/* Index tag */}
        <div className="absolute top-3 left-3 inline-flex items-center h-6 px-2 rounded-full bg-white/90 backdrop-blur text-[10.5px] font-semibold tracking-[0.14em] text-[#111111] uppercase">
          <span className="text-[#6E56E7] mr-1">{String(index + 1).padStart(2, '0')}</span>
          Leistung
        </div>
      </div>
      <div className="p-5">
        <div className="text-[14.5px] font-semibold text-[#111111] leading-snug">{title}</div>
        <div className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{desc}</div>
      </div>
    </article>
  );
};

const ServicesSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  return (
    <section ref={ref} className="w-full">
      <div className={`flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pp-reveal ${inView ? 'pp-reveal-in' : ''}`}>
        <div>
          <div className="text-[11px] font-semibold tracking-[0.22em] text-slate-400 uppercase mb-3">
            Unsere Leistungen
          </div>
          <h2 className="text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.1] font-black tracking-[-0.02em] text-[#111111] max-w-[720px]">
            Mehr als Telekommunikation – <span className="text-[#6E56E7]">bundesweit</span> für Ihre Marke im Einsatz.
          </h2>
        </div>
        <p className="text-[14px] leading-relaxed text-slate-500 max-w-[380px]">
          Individuell, flexibel und zuverlässig. Unser Leistungsspektrum reicht weit über die Telekommunikation hinaus.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {services.map((s, i) => (
          <ServiceCard key={s.title} {...s} index={i} inView={inView} />
        ))}
      </div>
    </section>
  );
};

export default ServicesSection;
